import { useMemo } from "react";
import { Stack, alpha, useTheme } from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { motion } from "framer-motion";
import { HeatDrop, MassDataPoint } from "src/store/api/generatedApi";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { defaultLayout } from "../../plotting";
import { Layout, PlotData } from "plotly.js";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useMaterialColor, useMaterialDisplayName } from "hooks/useMaterials";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { useMaterialSymbol } from "hooks/useMaterialSymbol";
import { formatArea } from "components/identifyEventsTool/utils/formatArea";

export type DropBreakdown = "mass" | "lifts";
export type DropGrouping = "combined" | "byCrane" | "byConsumptionArea";

interface Props {
  start: string;
  end: string;
  drops: HeatDrop[];
  dropBreakdown: DropBreakdown;
  scaleWeightSeries?: MassDataPoint[];
  dropGrouping: DropGrouping;
}

interface MappedSeries {
  timestamps: string[];
  values: number[];
}

/** Build a cumulative-by-material series map for a subset of drops. */
function buildCumulativeSeries(
  drops: HeatDrop[],
  dropBreakdown: DropBreakdown,
  drop_areas: string[],
  toLocalDatetime: (ts: string, fmt: string) => string
): Record<string, MappedSeries> {
  return drops.reduce<Record<string, MappedSeries>>((current, drop) => {
    const sameAreaGroup =
      (drop_areas.includes(drop.from_area) &&
        drop_areas.includes(drop.to_area)) ||
      (!drop_areas.includes(drop.from_area) &&
        !drop_areas.includes(drop.to_area));
    const sign = sameAreaGroup
      ? 0
      : drop_areas.includes(drop.from_area)
        ? -1
        : 1;
    const newValue = (dropBreakdown === "mass" ? drop.mass : 1) * sign;
    const formattedTimestamp = toLocalDatetime(
      drop.drop_timestamp,
      "YYYY-MM-DD HH:mm:ss"
    );
    const appendedExisting: [string, MappedSeries][] = Object.entries(
      current
    ).map(([material, { timestamps, values }]) => [
      material,
      {
        timestamps: [
          ...timestamps,
          ...(material === drop.material_name ? [formattedTimestamp] : []),
        ],
        values: [
          ...values,
          ...(material === drop.material_name
            ? [Math.max(...values) + newValue]
            : []),
        ],
      },
    ]);
    const newEntries: [string, MappedSeries][] = Object.keys(current).includes(
      drop.material_name
    )
      ? []
      : [
          [
            drop.material_name,
            { timestamps: [formattedTimestamp], values: [newValue] },
          ],
        ];
    return Object.fromEntries([...appendedExisting, ...newEntries]);
  }, {});
}

/** Build Plotly traces for one group of drops, targeting the given axis pair. */
function buildTraces(
  seriesByMaterial: Record<string, MappedSeries>,
  allTimestamps: string[],
  getColor: (m: string) => string,
  materialDisplayName: (m: string) => string,
  axisIndex: number, // 1-based; 1 → xaxis/yaxis, 2 → xaxis2/yaxis2, …
  stackgroupSuffix: string,
  seenMaterials: Set<string>
): Partial<PlotData>[] {
  const xaxisKey = axisIndex === 1 ? "x" : (`x${axisIndex}` as "x");
  const yaxisKey = axisIndex === 1 ? "y" : (`y${axisIndex}` as "y");
  return Object.entries(seriesByMaterial).map(
    ([material, { timestamps, values }]) => {
      const colour = getColor(material);
      const interpolated = allTimestamps.map((timestamp) => {
        const nextIdx = timestamps.findIndex((ts) => ts > timestamp);
        return (
          (nextIdx === -1 ? values[values.length - 1] : values[nextIdx - 1]) ??
          0
        );
      });
      const showlegend = !seenMaterials.has(material);
      seenMaterials.add(material);
      return {
        x: allTimestamps,
        y: interpolated,
        xaxis: xaxisKey,
        yaxis: yaxisKey,
        type: "scatter" as const,
        mode: "lines" as const,
        name: materialDisplayName(material),
        legendgroup: material,
        showlegend,
        line: { color: colour, shape: "hv" as const },
        marker: { color: colour },
        stackgroup: `stack-${stackgroupSuffix}`,
      };
    }
  );
}

export const HeatDropTimeline = ({
  start,
  end,
  drops,
  dropBreakdown,
  scaleWeightSeries,
  dropGrouping,
}: Props) => {
  const { getMaterialSymbol } = useMaterialSymbol();
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const { toLocalDatetime } = useLocalDatetime();
  const { getColor } = useMaterialColor();
  const materialDisplayName = useMaterialDisplayName();
  const units = useUnitsFormatter(true);
  const { drop_areas } = useScrapEyeMetadata();

  const hasScaleTraces = !!scaleWeightSeries?.length;

  const scaleValues = scaleWeightSeries?.map((p) => p.value) ?? [];
  const scaleMinValue = scaleValues.length ? Math.min(0, ...scaleValues) : 0;
  const scaleMaxValue = scaleValues.length
    ? Math.max(0.001, ...scaleValues)
    : 0.001;

  const yLabel =
    dropBreakdown === "mass"
      ? `${t("mixMass")}${units("mass")}`
      : t("totalLifts");
  const tickFormat = dropBreakdown === "mass" ? ".1f" : ".0f";

  const xRange = useMemo(
    () => [
      toLocalDatetime(start, "YYYY-MM-DD HH:mm:ss"),
      toLocalDatetime(end, "YYYY-MM-DD HH:mm:ss"),
    ],
    [toLocalDatetime, start, end]
  );

  const allTimestamps = useMemo(() => {
    const scaleTimes =
      scaleWeightSeries?.map(({ timestamp }) =>
        toLocalDatetime(timestamp, "YYYY-MM-DD HH:mm:ss")
      ) ?? [];
    return [
      ...new Set([
        toLocalDatetime(start, "YYYY-MM-DD HH:mm:ss"),
        ...drops.map(({ drop_timestamp }) =>
          toLocalDatetime(drop_timestamp, "YYYY-MM-DD HH:mm:ss")
        ),
        ...scaleTimes,
        toLocalDatetime(end, "YYYY-MM-DD HH:mm:ss"),
      ]),
    ].sort();
  }, [toLocalDatetime, start, end, drops, scaleWeightSeries]);

  const cumulativeSeriesByMaterial = useMemo(
    () =>
      drops.reduce<Record<string, MappedSeries>>((current, drop) => {
        const sameAreaGroup =
          (drop_areas.includes(drop.from_area) &&
            drop_areas.includes(drop.to_area)) ||
          (!drop_areas.includes(drop.from_area) &&
            !drop_areas.includes(drop.to_area));
        const sign = sameAreaGroup
          ? 0
          : drop_areas.includes(drop.from_area)
            ? -1
            : 1;
        const newValue = (dropBreakdown === "mass" ? drop.mass : 1) * sign;
        const formattedTimestamp = toLocalDatetime(
          drop.drop_timestamp,
          "YYYY-MM-DD HH:mm:ss"
        );
        const appendedExisting: [string, MappedSeries][] = Object.entries(
          current
        ).map(([material, { timestamps, values }]) => [
          material,
          {
            timestamps: [
              ...timestamps,
              ...(material === drop.material_name ? [formattedTimestamp] : []),
            ],
            values: [
              ...values,
              ...(material === drop.material_name
                ? [Math.max(...values) + newValue]
                : []),
            ],
          },
        ]);
        const newEntries: [string, MappedSeries][] = Object.keys(
          current
        ).includes(drop.material_name)
          ? []
          : [
              [
                drop.material_name,
                { timestamps: [formattedTimestamp], values: [newValue] },
              ],
            ];
        return Object.fromEntries([...appendedExisting, ...newEntries]);
      }, {}),
    [drops, drop_areas, dropBreakdown, toLocalDatetime]
  );

  // Step-function values for each material at every shared timestamp; shared
  // between plotData (visual traces) and totalAtTimestamps (hover total).
  const allValuesByMaterial = useMemo(
    () =>
      Object.entries(cumulativeSeriesByMaterial).map(
        ([material, { timestamps, values }]) => ({
          material,
          allValues: allTimestamps.map((timestamp) => {
            const nextIdx = timestamps.findIndex((ts) => ts > timestamp);
            return (
              (nextIdx === -1
                ? values[values.length - 1]
                : values[nextIdx - 1]) ?? 0
            );
          }),
        })
      ),
    [cumulativeSeriesByMaterial, allTimestamps]
  );

  // Running sum across all materials — powers the hover total row.
  const totalAtTimestamps = useMemo(
    () =>
      allTimestamps.map((_, i) =>
        allValuesByMaterial.reduce(
          (sum, { allValues }) => sum + (allValues[i] ?? 0),
          0
        )
      ),
    [allValuesByMaterial, allTimestamps]
  );

  const dropsMaxValue = useMemo(
    () => (totalAtTimestamps.length ? Math.max(0, ...totalAtTimestamps) : 0),
    [totalAtTimestamps]
  );

  const dropsMinValue = useMemo(
    () => (totalAtTimestamps.length ? Math.min(0, ...totalAtTimestamps) : 0),
    [totalAtTimestamps]
  );

  const plotData = useMemo(
    (): Partial<PlotData>[] =>
      allValuesByMaterial.map(({ material, allValues }) => {
        const colour = getColor(material);
        const name = materialDisplayName(material);
        return {
          x: allTimestamps,
          y: allValues,
          type: "scatter",
          mode: "lines",
          name,
          hovertemplate: `${name}: %{y:${tickFormat}}<extra></extra>`,
          line: {
            color: hasScaleTraces ? alpha(colour, 0.4) : colour,
            shape: "hv",
          },
          marker: { color: colour },
          fillcolor: hasScaleTraces ? alpha(colour, 0.2) : undefined,
          stackgroup: `all`,
        };
      }),
    [
      allValuesByMaterial,
      allTimestamps,
      getColor,
      materialDisplayName,
      hasScaleTraces,
      tickFormat,
    ]
  );

  const totalTrace = useMemo(
    (): Partial<PlotData> => ({
      x: allTimestamps,
      y: totalAtTimestamps,
      type: "scatter",
      mode: "none",
      showlegend: false,
      hovertemplate: `${t("ScrapEye")} ${t("total")}: %{y:${tickFormat}}<extra></extra>`,
    }),
    [allTimestamps, totalAtTimestamps, t, tickFormat]
  );

  const scaleTracePlotData = useMemo((): Partial<PlotData>[] => {
    if (!hasScaleTraces) {
      return [];
    }
    const byArea = scaleWeightSeries.reduce<
      Record<string, { timestamps: string[]; values: number[] }>
    >((acc, point) => {
      const ts = toLocalDatetime(point.timestamp, "YYYY-MM-DD HH:mm:ss");
      const existing = acc[point.area] ?? { timestamps: [], values: [] };
      return {
        ...acc,
        [point.area]: {
          timestamps: [...existing.timestamps, ts],
          values: [...existing.values, point.value],
        },
      };
    }, {});

    // Project scale values onto the shared allTimestamps grid so that hover
    // tooltips align with the ScrapEye cumulative traces.
    return Object.entries(byArea).map(([area, { timestamps, values }]) => {
      const projected = allTimestamps.map((ts) => {
        const nextIdx = timestamps.findIndex((t) => t > ts);
        if (nextIdx === -1) return values[values.length - 1] ?? 0;
        if (nextIdx === 0) return values[0] ?? 0;
        const ts0 = timestamps[nextIdx - 1];
        const ts1 = timestamps[nextIdx];
        if (ts0 === undefined || ts1 === undefined)
          return values[nextIdx - 1] ?? 0;
        // Parse display strings as UTC so fractions are viewer-timezone-agnostic,
        // consistent with how Plotly itself reads these axis coordinates.
        const parseMs = (s: string) => Date.parse(s.replace(" ", "T") + "Z");
        const t0 = parseMs(ts0);
        const t1 = parseMs(ts1);
        const t = parseMs(ts);
        const frac = (t - t0) / (t1 - t0);
        const v0 = values[nextIdx - 1] ?? 0;
        const v1 = values[nextIdx] ?? 0;
        return v0 + frac * (v1 - v0);
      });
      return {
        x: allTimestamps,
        y: projected,
        type: "scatter" as const,
        mode: "lines" as const,
        name: area,
        hovertemplate: `${area}: %{y:.2f}<extra></extra>`,
        yaxis: "y" as const,
        line: { shape: "linear" as const, width: 2 },
        showlegend: true,
      };
    });
  }, [hasScaleTraces, scaleWeightSeries, toLocalDatetime, allTimestamps]);

  const layout: Partial<Layout> = useMemo(() => {
    // The axis always extends at least to −10 t so the imperfection region is
    // permanently visible; also adapts if actual scale or drop values go lower.
    const scaleAxisMin = Math.min(scaleMinValue, -10);
    const axisFloor = Math.min(scaleAxisMin, dropsMinValue);

    const yaxisRange: [number, number] | undefined =
      dropBreakdown === "mass" && dropsMaxValue > 0
        ? [
            axisFloor,
            Math.max(dropsMaxValue, hasScaleTraces ? scaleMaxValue : 0) * 1.05,
          ]
        : undefined;

    return {
      ...defaultLayout,
      xaxis: {
        type: "date",
        title: { text: t("time") },
        range: xRange,
      },
      yaxis: {
        ...defaultLayout.yaxis,
        title: { text: yLabel },
        tickformat: tickFormat,
        ...(yaxisRange ? { range: yaxisRange } : {}),
      },
      legend: {
        ...defaultLayout.legend,
        orientation: "h",
        y: -0.2,
        xanchor: "center",
      },
      height: 500,
      margin: { t: 15 },
      shapes: drops.map(({ drop_timestamp }) => ({
        type: "line" as const,
        x0: toLocalDatetime(drop_timestamp, "YYYY-MM-DD HH:mm:ss"),
        x1: toLocalDatetime(drop_timestamp, "YYYY-MM-DD HH:mm:ss"),
        y0: 0,
        y1: 1,
        yref: "paper" as const,
        line: {
          color: hasScaleTraces
            ? theme.palette.grey[400]
            : theme.palette.grey[500],
          width: 1,
          dash: "dot",
        },
      })),
      annotations: drops.map(({ drop_timestamp, material_name }) => ({
        x: toLocalDatetime(drop_timestamp, "YYYY-MM-DD HH:mm:ss"),
        y: 1,
        text: getMaterialSymbol(material_name),
        font: { color: getColor(material_name) },
        ...(hasScaleTraces ? { opacity: 0.5 } : {}),
        showarrow: false,
        yref: "paper" as const,
        xanchor: "center" as const,
        yanchor: "bottom" as const,
      })),
    };
  }, [
    t,
    yLabel,
    tickFormat,
    drops,
    getColor,
    toLocalDatetime,
    xRange,
    getMaterialSymbol,
    theme,
    hasScaleTraces,
    dropBreakdown,
    scaleMinValue,
    scaleMaxValue,
    dropsMaxValue,
    dropsMinValue,
  ]);

  // Ordered group keys for the current grouping mode.
  const groupKeys = useMemo(() => {
    if (dropGrouping === "byCrane") {
      const seen = new Set<string>();
      const keys: string[] = [];
      for (const drop of drops) {
        const key = drop.location_name ?? "unknown";
        if (!seen.has(key)) {
          seen.add(key);
          keys.push(key);
        }
      }
      return keys;
    }
    if (dropGrouping === "byConsumptionArea") {
      const seen = new Set<string>();
      const keys: string[] = [];
      for (const drop of drops) {
        const key =
          drop.consumption_area ??
          (drop_areas.includes(drop.to_area)
            ? drop.to_area
            : drop_areas.includes(drop.from_area)
              ? drop.from_area
              : null);
        if (key !== null && !seen.has(key)) {
          seen.add(key);
          keys.push(key);
        }
      }
      return keys;
    }
    // combined
    return ["all"];
  }, [drops, drop_areas, dropGrouping]);

  // Grouped-subplot result; null when dropGrouping is "combined".
  const groupedResult = useMemo(() => {
    if (dropGrouping === "combined") return null;

    const N = groupKeys.length;
    const subplotHeight = 280;
    const totalHeight = Math.max(subplotHeight, N * subplotHeight);

    const shapes: Partial<Layout>["shapes"] = [];
    const annotations: Partial<Layout>["annotations"] = [];
    const traces: Partial<PlotData>[] = [];
    const axisOverrides: Record<string, object> = {};
    // Tracks which materials have had a legend entry emitted; shared across
    // all subplots so the first occurrence wins regardless of group order.
    const seenMaterials = new Set<string>();

    groupKeys.forEach((groupKey, rowIndex) => {
      const axisIndex = rowIndex + 1; // 1-based Plotly axis numbering
      const xaxisKey = (axisIndex === 1 ? "x" : `x${axisIndex}`) as "x";
      const yaxisKey = (axisIndex === 1 ? "y" : `y${axisIndex}`) as "y";

      const groupDrops =
        dropGrouping === "byCrane"
          ? drops.filter((d) => (d.location_name ?? "unknown") === groupKey)
          : drops.filter((d) => {
              const area =
                d.consumption_area ??
                (drop_areas.includes(d.to_area)
                  ? d.to_area
                  : drop_areas.includes(d.from_area)
                    ? d.from_area
                    : null);
              return area === groupKey;
            });

      const seriesByMaterial = buildCumulativeSeries(
        groupDrops,
        dropBreakdown,
        drop_areas,
        toLocalDatetime
      );

      // Show each material's legend entry on its first appearance across all subplots.
      const rawGroupTraces = buildTraces(
        seriesByMaterial,
        allTimestamps,
        getColor,
        materialDisplayName,
        axisIndex,
        groupKey,
        seenMaterials
      );
      // Fade material traces when scale weight is overlaid, matching combined view.
      const groupTraces = hasScaleTraces
        ? rawGroupTraces.map((trace) => {
            const color =
              typeof trace.marker?.color === "string"
                ? trace.marker.color
                : undefined;
            return color != null
              ? {
                  ...trace,
                  line: { ...trace.line, color: alpha(color, 0.4) },
                  fillcolor: alpha(color, 0.2),
                }
              : trace;
          })
        : rawGroupTraces;
      traces.push(...groupTraces);

      // Invisible total trace to show cumulative value in the hover tooltip.
      const groupTotalAtTimestamps = allTimestamps.map((_, i) =>
        rawGroupTraces.reduce(
          (sum, trace) => sum + ((trace.y as number[])[i] ?? 0),
          0
        )
      );
      traces.push({
        x: allTimestamps,
        y: groupTotalAtTimestamps,
        xaxis: xaxisKey,
        yaxis: yaxisKey,
        type: "scatter" as const,
        mode: "none" as const,
        showlegend: false,
        hovertemplate: `${t("ScrapEye")} ${t("total")}: %{y:${tickFormat}}<extra></extra>`,
      });

      // Scale weight trace for this subplot, if available.
      // scaleWeightSeries.area matches crane names (scale_weight_source=crane)
      // or consumption area names (scale_weight_source=bucket), so filtering
      // by groupKey automatically shows it only when the grouping aligns.
      if (hasScaleTraces) {
        const groupScalePoints = scaleWeightSeries.filter(
          (p) => p.area === groupKey
        );
        if (groupScalePoints.length > 0) {
          traces.push({
            x: groupScalePoints.map((p) =>
              toLocalDatetime(p.timestamp, "YYYY-MM-DD HH:mm:ss")
            ),
            y: groupScalePoints.map((p) => p.value),
            xaxis: xaxisKey,
            yaxis: yaxisKey,
            type: "scatter" as const,
            mode: "lines" as const,
            name: groupKey,
            hovertemplate: `${groupKey}: %{y:.2f}<extra></extra>`,
            line: { shape: "linear" as const, width: 2 },
            showlegend: true,
            legendgroup: `scale-${groupKey}`,
          });
        }
      }

      // Per-subplot axis configuration.
      const xaxisName = axisIndex === 1 ? "xaxis" : `xaxis${axisIndex}`;
      const yaxisName = axisIndex === 1 ? "yaxis" : `yaxis${axisIndex}`;
      axisOverrides[xaxisName] = {
        type: "date",
        title: rowIndex === N - 1 ? { text: t("time") } : undefined,
        range: xRange,
      };
      axisOverrides[yaxisName] = {
        ...defaultLayout.yaxis,
        title: { text: yLabel },
        tickformat: tickFormat,
      };

      const yaxisRef = yaxisKey;
      const xaxisRef = xaxisKey;
      const yDomainRef = `${yaxisRef} domain` as "paper";
      const xDomainRef = `${xaxisRef} domain` as "paper";

      // Group label at y: 1.08 in domain coords, material symbols at y: 1.
      // Both are above the subplot's plot area; the higher y keeps them on
      // separate bands so they never collide.
      annotations.push({
        text: `<b>${formatArea(groupKey)}</b>`,
        x: 0.01,
        y: 1.16,
        xref: xDomainRef,
        yref: yDomainRef,
        xanchor: "left" as const,
        yanchor: "bottom" as const,
        showarrow: false,
        font: { size: 12, color: theme.palette.text.secondary },
      });

      for (const { drop_timestamp, material_name } of groupDrops) {
        const xTs = toLocalDatetime(drop_timestamp, "YYYY-MM-DD HH:mm:ss");
        shapes.push({
          type: "line" as const,
          x0: xTs,
          x1: xTs,
          y0: 0,
          y1: 1,
          xref: xaxisRef,
          yref: yDomainRef,
          line: {
            color: hasScaleTraces
              ? theme.palette.grey[400]
              : theme.palette.grey[500],
            width: 1,
            dash: "dot",
          },
        });
        // Symbol sits just above the top of this subplot's domain.
        annotations.push({
          x: xTs,
          y: 1,
          xref: xaxisRef,
          yref: yDomainRef,
          text: getMaterialSymbol(material_name),
          font: { color: getColor(material_name) },
          ...(hasScaleTraces ? { opacity: 0.5 } : {}),
          showarrow: false,
          xanchor: "center" as const,
          yanchor: "bottom" as const,
        });
      }
    });

    const groupedLayout: Partial<Layout> = {
      ...defaultLayout,
      ...axisOverrides,
      grid: { rows: N, columns: 1, pattern: "independent", ygap: 0.35 },
      legend: {
        ...defaultLayout.legend,
        orientation: "h",
        y: -0.2,
        xanchor: "center",
        groupclick: "togglegroup",
      },
      height: totalHeight,
      margin: { t: 50, b: 100 },
      shapes,
      annotations,
    };

    return { groupedPlotData: traces, groupedLayout };
  }, [
    dropGrouping,
    groupKeys,
    drops,
    dropBreakdown,
    drop_areas,
    toLocalDatetime,
    allTimestamps,
    getColor,
    materialDisplayName,
    t,
    xRange,
    yLabel,
    tickFormat,
    getMaterialSymbol,
    theme,
    hasScaleTraces,
    scaleWeightSeries,
  ]);

  return (
    <Stack flex={1}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div style={{ width: "100%" }}>
          <Plot
            data={
              groupedResult
                ? groupedResult.groupedPlotData
                : [...plotData, totalTrace, ...scaleTracePlotData]
            }
            layout={groupedResult?.groupedLayout ?? layout}
            style={{ width: "100%" }}
            useResizeHandler
          />
        </div>
      </motion.div>
    </Stack>
  );
};
